import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
} from "@react-pdf/renderer";
import { getReportProducts } from "../../Reports/utils/reportUtils";

/* ══════════════════════════════════════════════════════════════
   Config
══════════════════════════════════════════════════════════════ */
export const ENTITY_FULL_NAME = process.env.REACT_APP_ENTITY_FULL_NAME;
export const CONFIG = { entityTitle: ENTITY_FULL_NAME };

export const buildFileName = ({ entityTitle, companyName, timestamp }) => {
  const slugify = (str) =>
    (str || "")
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");
  const parts = [entityTitle, companyName, "reports", timestamp]
    .map(slugify)
    .filter(Boolean)
    .filter((v, i, arr) => v !== arr[i - 1]);
  return `${parts.join("_") || "reports"}.pdf`;
};

/* ══════════════════════════════════════════════════════════════
   Palette
══════════════════════════════════════════════════════════════ */

const C = {
  black: "#18181b",
  dark: "#27272a",
  mid: "#52525b",
  muted: "#71717a",
  subtle: "#a1a1aa",
  border: "#d4d4d8",
  wash: "#f4f4f5",
  white: "#ffffff",
};

/* ══════════════════════════════════════════════════════════════
   Styles
══════════════════════════════════════════════════════════════ */

const styles = StyleSheet.create({
  page: {
    fontFamily: "Helvetica",
    fontSize: 10,
    color: C.dark,
    backgroundColor: C.white,
    paddingTop: 48,
    paddingBottom: 48,
    paddingHorizontal: 56,
    lineHeight: 1.55,
    flexDirection: "column",
  },
  topBar: {
    height: 4,
    backgroundColor: C.black,
    marginBottom: 28,
    marginHorizontal: -56,
  },

  /* ── cover ── */
  coverPage: {
    fontFamily: "Helvetica",
    fontSize: 10,
    color: C.dark,
    backgroundColor: C.white,
    padding: 56,
    flexDirection: "column",
    justifyContent: "center",
  },
  coverAccent: {
    height: 4,
    backgroundColor: C.black,
    marginBottom: 48,
    marginHorizontal: -56,
    marginTop: -56,
  },
  coverEntityTitle: {
    fontSize: 9,
    fontFamily: "Helvetica-Bold",
    color: C.subtle,
    textTransform: "uppercase",
    letterSpacing: 1.8,
    marginBottom: 10,
  },
  coverTitle: {
    fontSize: 28,
    fontFamily: "Helvetica-Bold",
    color: C.black,
    marginBottom: 8,
  },
  coverSub: {
    fontSize: 11,
    color: C.muted,
    marginBottom: 48,
  },
  coverDivider: {
    height: 1,
    backgroundColor: C.border,
    marginBottom: 24,
  },
  coverMeta: {
    fontSize: 8,
    color: C.subtle,
    lineHeight: 1.8,
  },

  /* ── report header ── */
  reportHeader: {
    marginBottom: 20,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: C.border,
    borderBottomStyle: "solid",
  },
  reportTitleRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: 6,
  },
  reportTitle: {
    fontSize: 18,
    fontFamily: "Helvetica-Bold",
    color: C.black,
    flexShrink: 1,
    flexGrow: 1,
    paddingRight: 16,
  },
  // FIX: asymmetric padding compensates for font descender rendering in react-pdf
  badge: {
    backgroundColor: C.wash,
    borderWidth: 1,
    borderColor: C.border,
    borderStyle: "solid",
    paddingHorizontal: 7,
    paddingTop: 3,
    paddingBottom: 3,
    fontSize: 7,
    lineHeight: 1, // tightens line box so padding renders evenly
    color: C.mid,
    fontFamily: "Helvetica-Bold",
    // no textTransform — uppercasing in JS avoids baseline shift bug
    letterSpacing: 0.6,
  },
  timestamp: {
    fontSize: 8,
    color: C.subtle,
    marginTop: 4,
  },

  /* ── content ──
     FIX: marginBottom on paragraph itself ensures consistent spacing
     regardless of whether the source content uses \n or \n\n between
     paragraphs. The spacer view handles extra blank lines on top of this.
  ── */
  paragraph: {
    fontSize: 10,
    color: C.dark,
    lineHeight: 1.7,
    marginBottom: 10,
  },
  paragraphSpacer: {
    height: 4, // extra breathing room for explicit blank lines in source
  },
  h1: {
    fontSize: 13,
    fontFamily: "Helvetica-Bold",
    color: C.black,
    marginTop: 16,
    marginBottom: 6,
  },
  h2: {
    fontSize: 9,
    fontFamily: "Helvetica-Bold",
    color: C.muted,
    textTransform: "uppercase",
    letterSpacing: 0.9,
    marginTop: 14,
    marginBottom: 4,
  },
  h3: {
    fontSize: 8.5,
    fontFamily: "Helvetica-Bold",
    color: C.subtle,
    textTransform: "uppercase",
    letterSpacing: 0.6,
    marginTop: 10,
    marginBottom: 3,
  },
  hr: {
    borderBottomWidth: 1,
    borderBottomColor: C.border,
    borderBottomStyle: "solid",
    marginVertical: 12,
  },
  bulletRow: {
    flexDirection: "row",
    marginBottom: 3,
    paddingLeft: 8,
  },
  bulletDot: { width: 12, fontSize: 10, color: C.subtle, lineHeight: 1.7 },
  bulletText: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    fontSize: 10,
    color: C.dark,
    lineHeight: 1.7,
  },

  /* ── table ── */
  table: {
    marginVertical: 8,
    borderWidth: 1,
    borderColor: C.border,
    borderStyle: "solid",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: C.border,
    borderBottomStyle: "solid",
  },
  tableRowLast: { flexDirection: "row" },
  tableHeaderCell: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    paddingHorizontal: 8,
    paddingVertical: 5,
    backgroundColor: C.wash,
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    color: C.mid,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    borderRightWidth: 1,
    borderRightColor: C.border,
    borderRightStyle: "solid",
  },
  tableHeaderCellLast: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    paddingHorizontal: 8,
    paddingVertical: 5,
    backgroundColor: C.wash,
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    color: C.mid,
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  tableCell: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    paddingHorizontal: 8,
    paddingVertical: 5,
    fontSize: 9,
    color: C.dark,
    lineHeight: 1.5,
    borderRightWidth: 1,
    borderRightColor: C.border,
    borderRightStyle: "solid",
  },
  tableCellLast: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    paddingHorizontal: 8,
    paddingVertical: 5,
    fontSize: 9,
    color: C.dark,
    lineHeight: 1.5,
  },

  /* ── products ── */
  productsSection: {
    marginTop: 16,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: C.border,
    borderTopStyle: "solid",
  },
  productsLabel: {
    fontSize: 7.5,
    fontFamily: "Helvetica-Bold",
    color: C.subtle,
    textTransform: "uppercase",
    letterSpacing: 0.9,
    marginBottom: 8,
  },
  productCard: {
    marginBottom: 12,
    padding: 10,
    borderWidth: 1,
    borderColor: C.border,
    borderStyle: "solid",
    backgroundColor: C.wash,
  },
  productTitle: {
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
    color: C.black,
    marginBottom: 4,
  },

  /* ── sources ── */
  sourcesSection: {
    marginTop: 16,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: C.border,
    borderTopStyle: "solid",
  },
  sourcesLabel: {
    fontSize: 7.5,
    fontFamily: "Helvetica-Bold",
    color: C.subtle,
    textTransform: "uppercase",
    letterSpacing: 0.9,
    marginBottom: 6,
  },
  sourceRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 3,
  },
  sourceIndex: {
    fontSize: 8,
    color: C.mid,
    fontFamily: "Helvetica-Bold",
    width: 20,
  },
  sourceText: {
    fontSize: 8,
    color: C.muted,
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
  },

  /* ── footer ── */
  pageFooter: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderTopWidth: 0.5,
    borderTopColor: C.border,
    borderTopStyle: "solid",
    paddingTop: 6,
    marginTop: "auto",
  },
  footerLeft: { fontSize: 7, color: C.subtle },
  footerRight: { fontSize: 7, color: C.subtle },
});

/* ══════════════════════════════════════════════════════════════
   Helpers
══════════════════════════════════════════════════════════════ */

const stripCitations = (text) => (text || "").replace(/\[\d+\]/g, "");
const stripBold = (text) =>
  (text || "").replace(/\*\*([^*]+)\*\*/g, "$1").replace(/\*([^*]+)\*/g, "$1");
const clean = (text) => stripBold(stripCitations(text)).trim();

const formatTs = (ts) => {
  if (!ts) return "";
  try {
    const num = typeof ts === "string" ? Number(ts) : ts;
    const date = new Date(
      typeof num === "number" && num < 1e12 ? num * 1000 : num,
    );
    if (isNaN(date.getTime())) return ts;
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return ts;
  }
};

/* ══════════════════════════════════════════════════════════════
   Table renderer
══════════════════════════════════════════════════════════════ */

const isTableLine = (line) =>
  line.trim().startsWith("|") && line.trim().endsWith("|");
const isSeparator = (line) => /^\|[\s|:-]+\|$/.test(line.trim());
const parseTableRow = (line) =>
  line.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map(clean);

const renderTable = (lines, key) => {
  const rows = lines.filter((l) => !isSeparator(l)).map(parseTableRow);
  if (rows.length === 0) return null;
  const [headerRow, ...bodyRows] = rows;
  const colCount = headerRow.length;
  return (
    <View key={key} style={styles.table}>
      <View style={styles.tableRow}>
        {headerRow.map((cell, ci) => (
          <Text
            key={ci}
            style={
              ci === colCount - 1
                ? styles.tableHeaderCellLast
                : styles.tableHeaderCell
            }
          >
            {cell}
          </Text>
        ))}
      </View>
      {bodyRows.map((row, ri) => {
        const isLast = ri === bodyRows.length - 1;
        const cells = Array.from(
          { length: colCount },
          (_, ci) => row[ci] || "",
        );
        return (
          <View key={ri} style={isLast ? styles.tableRowLast : styles.tableRow}>
            {cells.map((cell, ci) => (
              <Text
                key={ci}
                style={
                  ci === colCount - 1 ? styles.tableCellLast : styles.tableCell
                }
              >
                {cell}
              </Text>
            ))}
          </View>
        );
      })}
    </View>
  );
};

/* ══════════════════════════════════════════════════════════════
   Markdown → PDF nodes
══════════════════════════════════════════════════════════════ */

const renderContentToPDF = (rawContent) => {
  if (!rawContent) return null;
  const lines = rawContent.split("\n");
  const nodes = [];
  let i = 0;
  let lastWasBlank = false;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) {
      // Emit a small extra spacer for explicit blank lines, but dedupe consecutive ones
      if (!lastWasBlank) {
        nodes.push(<View key={`space-${i}`} style={styles.paragraphSpacer} />);
        lastWasBlank = true;
      }
      i++;
      continue;
    }

    lastWasBlank = false;

    if (/^-{3,}$/.test(trimmed)) {
      nodes.push(<View key={`hr-${i}`} style={styles.hr} />);
      i++;
      continue;
    }
    if (/^#{1,3} /.test(line)) {
      const level = line.match(/^(#{1,3}) /)[1].length;
      const text = clean(line.replace(/^#{1,3} /, ""));
      const s = level === 1 ? styles.h1 : level === 2 ? styles.h2 : styles.h3;
      nodes.push(
        <Text key={`h${level}-${i}`} style={s}>
          {text}
        </Text>,
      );
      i++;
      continue;
    }
    if (/^[-*] /.test(line)) {
      while (i < lines.length && /^[-*] /.test(lines[i])) {
        nodes.push(
          <View key={`bullet-${i}`} style={styles.bulletRow}>
            <Text style={styles.bulletDot}>·</Text>
            <Text style={styles.bulletText}>{clean(lines[i].slice(2))}</Text>
          </View>,
        );
        i++;
      }
      continue;
    }
    if (isTableLine(line)) {
      const tableLines = [];
      while (i < lines.length && isTableLine(lines[i])) {
        tableLines.push(lines[i]);
        i++;
      }
      nodes.push(renderTable(tableLines, `table-${i}`));
      continue;
    }
    nodes.push(
      <Text key={`p-${i}`} style={styles.paragraph}>
        {clean(line)}
      </Text>,
    );
    i++;
  }
  return nodes;
};

/* ══════════════════════════════════════════════════════════════
   Products (optional per section — title + risk per product)
══════════════════════════════════════════════════════════════ */

// Text only: product images live on the velocityisointake S3 bucket, which
// has CORS disabled, and react-pdf has to fetch() image bytes — they came
// out as blank boxes. The on-screen tile still shows them (<img> needs no
// CORS).
const ProductsSection = ({ products }) => {
  // Image-only products would print as empty cards.
  const withText = (Array.isArray(products) ? products : []).filter(
    (p) => p.title || p.risk,
  );
  if (withText.length === 0) return null;
  return (
    <View style={styles.productsSection}>
      <Text style={styles.productsLabel}>Products</Text>
      {withText.map((product, idx) => (
        <View key={idx} style={styles.productCard} wrap={false}>
          {product.title ? (
            <Text style={styles.productTitle}>{clean(product.title)}</Text>
          ) : null}
          {product.risk ? renderContentToPDF(product.risk) : null}
        </View>
      ))}
    </View>
  );
};

/* ══════════════════════════════════════════════════════════════
   Sources
══════════════════════════════════════════════════════════════ */

const SourcesSection = ({ sourcesList }) => {
  if (!Array.isArray(sourcesList) || sourcesList.length === 0) return null;
  return (
    <View style={styles.sourcesSection}>
      <Text style={styles.sourcesLabel}>Sources</Text>
      {sourcesList.map((s, idx) => {
        const url =
          typeof s === "string" ? s : s.value || s.url || s.doc_id || "";
        const display =
          (typeof s === "object" && s.title) ||
          url.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "");
        return (
          <View key={idx} style={styles.sourceRow}>
            <Text style={styles.sourceIndex}>[{idx + 1}]</Text>
            <Text style={styles.sourceText}>{display || url}</Text>
          </View>
        );
      })}
    </View>
  );
};

/* ══════════════════════════════════════════════════════════════
   Document
══════════════════════════════════════════════════════════════ */

const ReportPDF = ({ reports = [], companyName = "" }) => {
  const brandName = CONFIG.entityTitle || "";
  const generatedAt = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  return (
    <Document
      title={companyName || brandName || "Reports"}
      author={brandName}
      creator={brandName}
    >
      {/* ── Cover page ── */}
      <Page size="A4" style={styles.coverPage}>
        <View style={styles.coverAccent} />
        {brandName ? (
          <Text style={styles.coverEntityTitle}>{brandName}</Text>
        ) : null}
        <Text style={styles.coverTitle}>{companyName || "Reports"}</Text>
        <Text style={styles.coverSub}>Research Reports · {generatedAt}</Text>
        <View style={styles.coverDivider} />
        <Text style={styles.coverMeta}>
          {reports.length} report{reports.length !== 1 ? "s" : ""}
          {"\n"}
          {reports
            .map((r) => r.title || r.section_title || r.target_type || "Report")
            .join("  ·  ")}
        </Text>
      </Page>

      {/* ── One page per report ── */}
      {reports.map((report, idx) => {
        const selectedVersion =
          (report.versions && report.versions[0]) || report;
        const targetType = (report.target_type || "").trim();
        const sectionTitle =
          (report.title || "").trim() ||
          (report.section_title || "").trim() ||
          targetType.replace(/_/g, " ");
        // Strip the trailing inline "Sources:" block — sources render
        // structured below (new API sends them as `sources` URL strings).
        const content = (selectedVersion.content || "")
          .replace(/\n+Sources:\s*[\s\S]*$/i, "")
          .trim();
        const sourcesList = [
          ...new Set(
            selectedVersion.sources_list ||
              selectedVersion.sources ||
              [],
          ),
        ];
        const displayType = targetType ? targetType.replace(/_/g, " ") : "";
        const timestamp = formatTs(selectedVersion.timestamp);
        const products = getReportProducts(selectedVersion);

        return (
          <Page key={idx} size="A4" style={styles.page}>
            <View style={styles.topBar} />

            <View style={styles.reportHeader}>
              <View style={styles.reportTitleRow}>
                <Text style={styles.reportTitle}>
                  {sectionTitle || "Report"}
                </Text>
                {displayType ? (
                  <Text style={styles.badge}>{displayType.toUpperCase()}</Text>
                ) : null}
              </View>
              {timestamp ? (
                <Text style={styles.timestamp}>{timestamp}</Text>
              ) : null}
            </View>

            {renderContentToPDF(content)}
            <ProductsSection products={products} />
            <SourcesSection sourcesList={sourcesList} />

            <View style={styles.pageFooter} fixed>
              <Text style={styles.footerLeft}>
                {[brandName, companyName].filter(Boolean).join(" · ")}
              </Text>
              <Text
                style={styles.footerRight}
                render={({ pageNumber, totalPages }) =>
                  `${sectionTitle}  ·  ${pageNumber} / ${totalPages}`
                }
              />
            </View>
          </Page>
        );
      })}
    </Document>
  );
};

export default ReportPDF;
